import { ARCHITECTURE_VERSION, type ArchitectureVersion } from '@/utils/constants';
import type { EsrEntry } from '@/utils/helpers';

/**
 * Parse the `architecture` query/URL param. Returns either the parsed
 * architecture value, `null` (param absent or empty), or an `error` string
 * for the caller to surface as a 400.
 *
 * Accepted values: `'1' | '2' | 'custom'`. Anything else is rejected.
 */
export function parseArchitectureParam(
  raw: string | null | undefined,
): { value: ArchitectureVersion | null } | { error: string } {
  if (raw == null) return { value: null };
  const t = raw.trim();
  if (t === '') return { value: null };
  if (
    t === ARCHITECTURE_VERSION.A1 ||
    t === ARCHITECTURE_VERSION.A2 ||
    t === ARCHITECTURE_VERSION.CUSTOM
  ) {
    return { value: t };
  }
  return { error: "architecture must be '1', '2', or 'custom' when provided" };
}

/**
 * Map a partner/query `architecture` opt-in to the `architectures` filter
 * array the RPC + `/load/model` matcher accept. Single source of truth for
 * the four-way dispatch:
 *
 *   '2'       → ['2']
 *   '1'       → ['1']
 *   'custom'  → ['custom']
 *   undefined → ['1', 'custom']   (legacy default — excludes A2 so partners
 *                                  that pre-date A2 don't start receiving
 *                                  models they can't load)
 *
 * For single-model checks, use `array.includes(model.architecture_version)`.
 */
export function architecturesFilterFor(
  architecture: ArchitectureVersion | null | undefined,
): ArchitectureVersion[] {
  if (architecture === ARCHITECTURE_VERSION.A2) return [ARCHITECTURE_VERSION.A2];
  if (architecture === ARCHITECTURE_VERSION.A1) return [ARCHITECTURE_VERSION.A1];
  if (architecture === ARCHITECTURE_VERSION.CUSTOM) return [ARCHITECTURE_VERSION.CUSTOM];
  return [ARCHITECTURE_VERSION.A1, ARCHITECTURE_VERSION.CUSTOM];
}

/**
 * Filter a NAM tone's `models` array by the requested architecture. Pass-
 * through for non-NAM tones (their `architecture_version` is null and the
 * filter is meaningless).
 *
 * No-arch ('none' / undefined) → A1 + Custom (legacy "everything except A2").
 * Other values → strict equality on the corresponding enum.
 */
export function filterModelsByArchitecture<T extends { architecture_version?: string | null }>(
  models: T[],
  platform: string | null | undefined,
  architecture: ArchitectureVersion | null | undefined,
): T[] {
  if (platform !== 'nam') return models;
  if (architecture === ARCHITECTURE_VERSION.A2) {
    return models.filter((m) => m.architecture_version === ARCHITECTURE_VERSION.A2);
  }
  if (architecture === ARCHITECTURE_VERSION.A1) {
    return models.filter((m) => m.architecture_version === ARCHITECTURE_VERSION.A1);
  }
  if (architecture === ARCHITECTURE_VERSION.CUSTOM) {
    return models.filter((m) => m.architecture_version === ARCHITECTURE_VERSION.CUSTOM);
  }
  return models.filter((m) => m.architecture_version !== ARCHITECTURE_VERSION.A2);
}

/**
 * Visible model count for a tone, given the architecture context.
 *
 * NAM tones:
 *   undefined → a1 + custom   (legacy "everything except A2", preserves the partner default)
 *   '1'       → a1
 *   '2'       → a2
 *   'custom'  → custom
 *
 * Non-NAM tones (ir, aida-x, aa-snapshot, proteus): always models_count —
 * architecture has no semantic meaning here.
 */
export function visibleModelsCount(
  tone: {
    models_count?: number | null;
    a1_models_count?: number | null;
    a2_models_count?: number | null;
    custom_models_count?: number | null;
    platform?: string | null;
  },
  architecture: ArchitectureVersion | undefined | null,
): number {
  if (tone.platform !== 'nam') return tone.models_count ?? 0;
  if (architecture === ARCHITECTURE_VERSION.A1) return tone.a1_models_count ?? 0;
  if (architecture === ARCHITECTURE_VERSION.A2) return tone.a2_models_count ?? 0;
  if (architecture === ARCHITECTURE_VERSION.CUSTOM) return tone.custom_models_count ?? 0;
  return (tone.a1_models_count ?? 0) + (tone.custom_models_count ?? 0);
}

/**
 * Display count for tone cards / search rows on the **core site**, mirroring
 * the tone-detail page's top-count rule so list views match detail views:
 *
 *   - NAM with A2 → `a2_models_count`
 *   - NAM without A2 → `null` (caller renders em-dash)
 *   - Non-NAM → `models_count`
 *
 * Returns `null` for the no-A2 NAM case so the caller can render em-dash
 * exactly like the detail page does. Partner/Select surfaces use
 * `visibleModelsCount` instead to preserve the legacy partner contract.
 */
export function preferredModelsCount(tone: {
  models_count?: number | null;
  a2_models_count?: number | null;
  platform?: string | null;
}): number | null {
  if (tone.platform !== 'nam') return tone.models_count ?? 0;
  if ((tone.a2_models_count ?? 0) > 0) return tone.a2_models_count ?? 0;
  return null;
}

/**
 * Default selection for the segmented control. Prefers A2 (the new first-class
 * citizen), falling back to whichever architecture has models, defaulting to
 * A2 when the tone has none at all (the segmented control still renders).
 */
export function preferredArchitecture(tone: {
  a1_models_count?: number | null;
  a2_models_count?: number | null;
  custom_models_count?: number | null;
}): ArchitectureVersion {
  if ((tone.a2_models_count ?? 0) > 0) return ARCHITECTURE_VERSION.A2;
  if ((tone.a1_models_count ?? 0) > 0) return ARCHITECTURE_VERSION.A1;
  if ((tone.custom_models_count ?? 0) > 0) return ARCHITECTURE_VERSION.CUSTOM;
  return ARCHITECTURE_VERSION.A2;
}

/** `trainings.esrs[].max_value` → display size label. */
const MAX_VALUE_LABELS: Array<[number, string]> = [
  [0.5, 'Lite'],
  [1.0, 'Full'],
];

export function esrSizeLabel(maxValue: number): string {
  let best = MAX_VALUE_LABELS[0];
  let bestDist = Math.abs(maxValue - best[0]);
  for (const entry of MAX_VALUE_LABELS) {
    const dist = Math.abs(maxValue - entry[0]);
    if (dist < bestDist) {
      best = entry;
      bestDist = dist;
    }
  }
  return best[1];
}

/** Sort `esrs` entries Full first, then Lite (descending `max_value`). */
export function sortedEsrs(esrs: EsrEntry[]): EsrEntry[] {
  return [...esrs].sort((a, b) => b.max_value - a.max_value);
}
